import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Package, Truck, ArrowLeft, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending_shipment", label: "Pending shipment" },
  { id: "shipped", label: "Shipped" },
  { id: "fulfilled", label: "Fulfilled (digital)" },
];

export default function SellerOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending_shipment");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [tracking, setTracking] = useState({ tracking_number: "", carrier: "" });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const res = await axios.get(`${API}/seller/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setOrders(res.data?.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchOrders(); }, [token, filter]);

  const handleMarkShipped = async (orderId) => {
    try {
      await axios.post(
        `${API}/seller/orders/${orderId}/mark-shipped`,
        { tracking_number: tracking.tracking_number || null, carrier: tracking.carrier || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Order marked as shipped — buyer notified");
      setEditingId(null);
      setTracking({ tracking_number: "", carrier: "" });
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark as shipped");
    }
  };

  const copyAddress = (addr) => {
    if (!addr) { toast.error("No address on file"); return; }
    const lines = [
      addr.name,
      addr.line1,
      addr.line2,
      [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", "),
      addr.country,
      addr.phone ? `Phone: ${addr.phone}` : null,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines);
    toast.success("Address copied");
  };

  const renderAddress = (addr) => {
    if (!addr || !addr.line1) {
      return <span className="text-[#6A7280] text-xs italic">No shipping address (digital item)</span>;
    }
    return (
      <div className="text-sm text-white leading-relaxed">
        {addr.name && <p className="font-semibold">{addr.name}</p>}
        <p>{addr.line1}</p>
        {addr.line2 && <p>{addr.line2}</p>}
        <p>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</p>
        <p className="uppercase text-[#8A92A0]">{addr.country}</p>
        {addr.phone && <p className="text-[#8A92A0] text-xs mt-1">📞 {addr.phone}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white pb-20" data-testid="seller-orders-page">
      <header className="sticky top-0 z-30 glass-strong border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-[#8A92A0] hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Package className="w-6 h-6 text-[#EC008C]" />
          <h1 className="font-['Unbounded'] text-xl font-bold">Shipping Dashboard</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <p className="text-[#8A92A0] text-sm mb-4">
          Physical orders (hoodies, vinyl, demo packs) show the buyer&apos;s shipping address automatically.
          Digital and experience orders appear in &quot;Fulfilled&quot; for your records.
        </p>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f.id ? "bg-[#EC008C] text-black" : "bg-[#0A0E18] text-[#8A92A0] hover:text-white border border-[#1A2030]"}`}
              data-testid={`filter-${f.id}`}
            >{f.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-[#8A92A0] py-12">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="card p-10 text-center" data-testid="orders-empty">
            <Truck className="w-10 h-10 text-[#EC008C] mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">No orders to ship</p>
            <p className="text-[#8A92A0] text-sm">New paid orders for physical items will land here automatically.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map(o => (
              <div
                key={o.order_id}
                className="card p-5 grid grid-cols-1 md:grid-cols-[1.2fr_1fr_auto] gap-4 items-start"
                data-testid={`order-row-${o.order_id}`}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      o.status === "shipped" ? "bg-emerald-500/15 text-emerald-400" :
                      o.status === "fulfilled" ? "bg-blue-500/15 text-blue-400" :
                      "bg-[#EC008C]/15 text-[#EC008C]"
                    }`}>{(o.status || "").replace("_", " ")}</span>
                    {o.requires_shipping && (
                      <span className="text-[10px] bg-[#1A2030] text-[#8A92A0] px-2 py-0.5 rounded-full">
                        Physical · {o.product_category}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-white" data-testid={`order-product-${o.order_id}`}>{o.product_name}</h3>
                  <p className="text-[#8A92A0] text-xs mt-0.5">
                    Buyer: <span className="text-white" data-testid={`order-buyer-${o.order_id}`}>{o.buyer_name || o.buyer_email}</span> · Qty {o.quantity}
                  </p>
                  <p className="text-[#8A92A0] text-xs mt-0.5">
                    ${(o.amount || 0).toFixed(2)} {(o.currency || "usd").toUpperCase()} ·{" "}
                    {new Date(o.created_at).toLocaleString()}
                  </p>
                  {o.tracking_number && (
                    <p className="text-emerald-400 text-xs mt-2" data-testid={`order-tracking-${o.order_id}`}>
                      <CheckCircle2 className="w-3 h-3 inline mr-1" />
                      Shipped via {o.carrier || "carrier"} · {o.tracking_number}
                    </p>
                  )}
                </div>

                <div className="border-l border-[#1A2030] pl-4" data-testid={`order-shipping-${o.order_id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A92A0] mb-1.5">Ship to</p>
                  {renderAddress(o.shipping_address)}
                  {o.shipping_address?.line1 && (
                    <button
                      onClick={() => copyAddress(o.shipping_address)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#EC008C] hover:underline"
                      data-testid={`copy-address-${o.order_id}`}
                    >
                      <Copy className="w-3 h-3" /> Copy address
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {o.status === "pending_shipment" ? (
                    editingId === o.order_id ? (
                      <div className="space-y-2 min-w-[180px]">
                        <Input
                          placeholder="Tracking #"
                          value={tracking.tracking_number}
                          onChange={(e) => setTracking({ ...tracking, tracking_number: e.target.value })}
                          className="bg-[#0A0E18] border-[#1A2030] text-white text-sm"
                          data-testid={`tracking-input-${o.order_id}`}
                        />
                        <Input
                          placeholder="Carrier (USPS, UPS…)"
                          value={tracking.carrier}
                          onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })}
                          className="bg-[#0A0E18] border-[#1A2030] text-white text-sm"
                          data-testid={`carrier-input-${o.order_id}`}
                        />
                        <div className="flex gap-1.5">
                          <Button
                            onClick={() => handleMarkShipped(o.order_id)}
                            className="flex-1 bg-[#EC008C] text-black hover:bg-[#FF3399] text-xs"
                            data-testid={`confirm-ship-${o.order_id}`}
                          >Mark shipped</Button>
                          <Button
                            onClick={() => { setEditingId(null); setTracking({ tracking_number: "", carrier: "" }); }}
                            variant="outline"
                            className="text-xs"
                          >Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setEditingId(o.order_id)}
                        className="bg-[#EC008C] text-black hover:bg-[#FF3399]"
                        data-testid={`mark-shipped-btn-${o.order_id}`}
                      >
                        <Truck className="w-4 h-4 mr-1.5" /> Mark shipped
                      </Button>
                    )
                  ) : (
                    <span className="text-[#6A7280] text-xs italic">No action needed</span>
                  )}
                  <Link
                    to={`/products/${o.product_id}`}
                    className="text-xs text-[#8A92A0] hover:text-white inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> View product
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
